import ChannelInterface from "@/interfaces/channel.interface";
import { TemplateInterface } from "@/interfaces/template.interface";
import ContactListInterface from "@/interfaces/contactList.interface";
import { parseWithMap, stringifyWithMap } from "@/utils/mapJsonParser";
import { readonly, Ref, ref } from "@vue/composition-api";
import lodash from "lodash";
import { getNewForm, reconstructForm } from "./formGenerator";
import { CampaignsInterface } from "./interfaces/campaigns";
import { EngageFormInterface } from "./interfaces/engageFormInterface";
import { EngageStepInterface } from "./interfaces/engageStepInterface";
// @ts-ignore
import router from "@/router";
import useCampaigns from "./useCampaigns";
//@ts-ignore
import i18n from "@/libs/i18n";
//@ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import { useToast } from "vue-toastification/composition";
import { CampaignAttachmentsInterface } from "@/views/modules/engage/interfaces/campaignAttachments";
import { DateTime } from "luxon";
import useTemplates from "@/composables/v3/useTemplates";
import useContactsLists from "@/composables/useContactsLists";
import ContactInterface from "@/interfaces/contact.interface";
import useUserSettings from "@/composables/useUserSettings";
import { getUserData } from "@/auth/getUserData";

const FORM_STAGE_KEY = "engage-form";

let stage = getNewForm();

const form: Ref<EngageFormInterface> = ref(reconstructForm(stage));
const currentStep: Ref<EngageStepInterface> = ref(form.value.steps[0]);
const selectedEngageToEdit: Ref<CampaignsInterface> = ref(null);

export default function useEngageForm(bvModal = undefined) {
    const { postCampaign, isSending, checkLimit, getCampaignById, putCampaign } = useCampaigns();
    const toast = useToast();
    const { isTemplateValid } = useTemplates();
    const { createContactList, deleteListNoDialog } = useContactsLists();

    const { getUserSetting } = useUserSettings();

    const save = (): void => {
        const data = { ...stage };

        for (const property in data) {
            if (lodash.get(data[property as keyof typeof data], "persist") === false) {
                delete data[property as keyof typeof data];
            }
        }

        localStorage.setItem(FORM_STAGE_KEY, stringifyWithMap(data));
    };

    const nextStep = async (): Promise<boolean> => {
        if (currentStep.value.id === 8) {
            if (stage.scheduleStep.isScheduled && stage.scheduleStep.dateTime < DateTime.now()) {
                stage.scheduleStep.isDone = false;
                toast.clear(); // Remove all existing toast before showing a new one.
                toast(
                    {
                        component: toastificationContent,
                        props: {
                            title: i18n.t("Error"),
                            html: i18n.t("engage.schedule.error.bad-date"),
                            icon: "times",
                            variant: "danger",
                        },
                    },
                    {
                        timeout: false,
                    }
                );
                return false;
            }
        } else if (currentStep.value.id === 3) {
            manageCharacterMessageLength();
        }
        if (currentStep.value.nextStepId === null) {
            sendEnagageMessage(() => {
                router.push({ name: "engage" });
            });
            return false;
        }
        currentStep.value.isDone = true;
        currentStep.value.isActive = false;

        if (currentStep.value.action) {
            await currentStep.value.action();
            return true;
        }
        currentStep.value =
            form.value.steps.find((step: EngageStepInterface) => step.id === currentStep.value.nextStepId) ||
            form.value.steps[0];
        currentStep.value.isActive = true;
        stage.currentStepId = currentStep.value.id;
        currentStep.value.validate();
        save();
        return true;
    };

    const previousStep = (): boolean => {
        if (currentStep.value.previousStepId === null) {
            router.push({ name: "engage" });
            return false;
        }
        currentStep.value.isDone = false;
        currentStep.value.isActive = false;

        if (currentStep.value.previousAction) {
            currentStep.value.previousAction();
            return true;
        }

        currentStep.value =
            form.value.steps.find((step: EngageStepInterface) => step.id === currentStep.value.previousStepId) ||
            form.value.steps[0];
        form.value.steps.forEach((step: EngageStepInterface) => {
            if (step.order > currentStep.value.order) {
                step.isDone = false;
            }
        });

        stage.currentStepId = currentStep.value.id;
        currentStep.value.isActive = true;

        currentStep.value.validate();
        save();
        return true;
    };

    const goToStep = (stepId: number): boolean => {
        const stepToGo =
            form.value.steps.find((step: EngageStepInterface) => step.id === stepId) || form.value.steps[0];

        currentStep.value.isActive = false;
        currentStep.value = stepToGo;

        if (!currentStep.value.independent) {
            previousStep();
            return true;
        }

        form.value.steps.forEach((step: EngageStepInterface) => {
            if (step.order > stepToGo.order) {
                step.isDone = false;
            }
        });

        stage.currentStepId = currentStep.value.id;
        currentStep.value.isActive = true;

        currentStep.value.validate();
        save();
        return true;
    };

    const clearData = (): void => {
        localStorage.removeItem(FORM_STAGE_KEY);
        stage = getNewForm();
        form.value = reconstructForm(stage);
        currentStep.value = form.value.steps[0];
        selectedEngageToEdit.value = null;
    };

    const selectContactList = async (contactList: ContactListInterface, proceed = false) => {
        stage.selectContactListStep.contactList = contactList;
        stage.selectContactListStep.isDone = false;

        const limits = await checkLimit(contactList.contacts.map((contact) => contact.id));
        toast.clear(); // Remove all existing toast before showing a new one.
        if (limits.contacts.isExceeded) {
            toast(
                {
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.new-engage-message"),
                        html: i18n.t("engage.limit-select-contacts-description", {
                            total: contactList.contacts.length + limits.contacts.currentValue,
                            limit: limits.contacts.limit,
                            email: `<a href='mailto:cs@saysimple.com?subject=${i18n.t(
                                "engage.rate-limits.info.e-mail-subject"
                            )}'>cs@saysimple.com</a>`,
                        }),
                        button: {
                            href: `mailto:cs@saysimple.com?subject=${i18n.t("engage.rate-limits.info.e-mail-subject")}`,
                            text: i18n.t("Upgrade"),
                        },
                        icon: "times",
                        variant: "danger",
                    },
                },
                {
                    timeout: false,
                }
            );
        }

        if (limits.messages.isExceeded) {
            toast(
                {
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.new-engage-message"),
                        html: i18n.t("engage.limit-select-messages-description", {
                            messagesUsed: limits.messages.currentValue,
                            messagesLimit: limits.messages.limit,
                            email: `<a href='mailto:cs@saysimple.com?subject=${i18n.t(
                                "engage.rate-limits.info.e-mail-subject"
                            )}'>cs@saysimple.com</a>`,
                        }),
                        button: {
                            href: `mailto:cs@saysimple.com?subject=${i18n.t("engage.rate-limits.info.e-mail-subject")}`,
                            text: i18n.t("Upgrade"),
                        },
                        icon: "times",
                        variant: "danger",
                    },
                },
                {
                    timeout: false,
                }
            );
        }

        stage.selectContactListStep.contactsLimit = limits.contacts.limit;
        stage.selectContactListStep.currentContactsValue = limits.contacts.currentValue;
        stage.selectContactListStep.messagesLimit = limits.messages.limit;
        stage.selectContactListStep.currentMessagesValue = limits.contacts.currentValue;
        stage.selectContactListStep.isLimitExceeded = limits.contacts.isExceeded || limits.messages.isExceeded;
        stage.selectContactListStep.validate();
        save();

        if (proceed) {
            nextStep();
        }
    };

    const selectTemplate = (info: { template: TemplateInterface; proceed?: boolean }): void => {
        const { proceed, template } = info;

        if (stage.selectTemplateStep.template?.id !== template.id || selectedEngageToEdit.value) {
            stage.selectTemplateStep.template = template;
            stage.selectTemplateStep.validate();

            stage.editTemplateStep.template = { ...template };
            const variableRegex = /{{[^}]+}}/g;
            let templateSkeleton = stage.editTemplateStep.template.content;
            let foundRegex;
            let variableId = 0;
            const variableMap = new Map<number, string>();

            // Based on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/exec
            // This is the best way to loop through regex.exec i don't know if this can be done prettier
            while ((foundRegex = variableRegex.exec(templateSkeleton)) !== null) {
                // Replace variable in the skeleton with __variableId:${variableId}__
                // I can't use curly braces otherwise it can result in an endless loop
                templateSkeleton = `${templateSkeleton.substring(
                    0,
                    foundRegex.index
                )}__variableId:${variableId}__${templateSkeleton.substring(foundRegex.index + foundRegex[0].length)}`;

                variableMap.set(variableId++, foundRegex[0]);
            }

            stage.editTemplateStep.templateSkeleton = templateSkeleton;
            stage.editTemplateStep.variables = variableMap;
            stage.editTemplateStep.attachments = [];
            stage.editTemplateStep.isDone = false;
            save();
        }

        if (proceed) {
            nextStep();
        }

        revalidateTemplateMessage();
    };

    const revalidateTemplateMessage = () => {
        const newContent = stage.editTemplateStep.templateSkeleton.replace(
            /__variableId:(?<id>\d+)__/g,
            (_, id) => stage.editTemplateStep.variables.get(Number(id)) ?? ""
        );

        const [isValid, validationMessage] = isTemplateValid(
            stage.editTemplateStep.template.originalContent,
            newContent,
            stage.selectChannelStep.channel
        );

        stage.editTemplateStep.validate();

        if (!isValid && validationMessage) {
            stage.editTemplateStep.isDone = false;

            toast.clear(); // Remove all existing toast before showing a new one.
            toast(
                {
                    component: toastificationContent,
                    props: {
                        title: i18n.t("engage.new-engage-message"),
                        html: validationMessage,
                        icon: "times",
                        variant: "danger",
                    },
                },
                {
                    timeout: false,
                }
            );
        } else if (isValid) {
            stage.editTemplateStep.isDone = true;
            stage.editTemplateStep.template.content = newContent;
        }
        save();
    };

    const editTemplate = (info: { proceed?: boolean; cancel?: boolean; variable?: number; value?: string }): void => {
        const { proceed, cancel, variable, value } = info;
        if (variable !== undefined) {
            stage.editTemplateStep.variables.set(variable, value || "");
        }

        revalidateTemplateMessage();

        if (proceed && stage.editTemplateStep.isDone) {
            nextStep();
        } else if (cancel) {
            previousStep();
        }
    };

    const setCampaignAttachments = (info: { attachments?: CampaignAttachmentsInterface[] }): void => {
        const { attachments } = info;

        stage.editTemplateStep.attachments = attachments;

        stage.editTemplateStep.validate();
        save();
    };

    const selectChannel = (info: { channel: ChannelInterface; proceed?: boolean }): void => {
        const { proceed, channel } = info;
        if (stage.selectChannelStep.channel?.id !== channel.id) {
            stage.selectChannelStep.channel = channel;
            stage.selectTemplateStep.channelId = channel.id;
            stage.selectChannelStep.validate();
            save();
        }

        if (proceed) {
            nextStep();
        }
    };

    const selectNameTimeout: Ref<NodeJS.Timeout | null> = ref(null);

    const selectName = (info: { name: string; description: string }): void => {
        const { name, description } = info;
        stage.nameEngageStep.messageName = name;
        stage.nameEngageStep.description = description;
        stage.nameEngageStep.validate();

        if (selectNameTimeout.value) {
            clearTimeout(selectNameTimeout.value);
        }

        selectNameTimeout.value = setTimeout(() => {
            save();
        }, 200);
    };

    const sendEnagageMessage = async (afterSend: () => void): Promise<CampaignsInterface | undefined> => {
        const campaign: CampaignsInterface = {
            channelId: stage.selectChannelStep.channel?.id,
            contactListId: stage.selectContactListStep.contactList?.id,
            entry: stage.editTemplateStep.template?.content,
            messageComponents: stage.editTemplateStep.template?.components,
            name: stage.nameEngageStep.messageName,
            description: stage.nameEngageStep.description,
            attachment: stage.editTemplateStep.attachments,
            templateId: stage.editTemplateStep.template?.id,
            sendAt: stage.scheduleStep.dateTime,
        };
        // in edit case we need to set the id
        if (selectedEngageToEdit.value) {
            campaign.id = selectedEngageToEdit.value.id;
        }
        // if selectedEngageToEdit is not null that mean we are in edit mode not in create
        const data = selectedEngageToEdit.value ? await putCampaign(campaign) : await postCampaign(campaign);
        if (stage.selectContactListStep.listIsTemporary) {
            // we delete the contactList like that it will not appear on the contact list table.
            await deleteListNoDialog(stage.selectContactListStep.contactList.id);
        }
        afterSend();
        return data;
    };

    const load = (): void => {
        const savedStage = localStorage.getItem(FORM_STAGE_KEY);

        if (savedStage !== null) {
            try {
                stage = parseWithMap(savedStage);
                form.value = reconstructForm(stage);
                currentStep.value =
                    form.value.steps.find((step: EngageStepInterface) => step.id === stage.currentStepId) ||
                    form.value.steps[0];
            } catch (e) {
                stage = getNewForm();
                form.value = reconstructForm(stage);
                currentStep.value = form.value.steps[0];
            }
        }

        if (currentStep.value.id === stage.selectContactListStep.id && stage.selectContactListStep.contactList) {
            selectContactList(stage.selectContactListStep.contactList);
        }
    };

    const selectSchedule = (dateTimeSchedule: DateTime): void => {
        stage.scheduleStep.dateTime = dateTimeSchedule;
        if (dateTimeSchedule > DateTime.now()) {
            stage.scheduleStep.isDone = true;
            save();
        }
    };

    const setScheduleIsEnabled = (isScheduled: boolean): void => {
        stage.scheduleStep.isScheduled = isScheduled;
        if (!isScheduled) {
            stage.scheduleStep.isDone = true;
        } else if (isScheduled && stage.scheduleStep.dateTime < DateTime.now()) {
            stage.scheduleStep.isDone = false;
        }
        save();
    };

    const getDateTimeSchedule = () => {
        return stage.scheduleStep?.dateTime || null;
    };

    const editCampaign = async (campaignId) => {
        clearData();
        selectedEngageToEdit.value = await getCampaignById(campaignId);
        router.push({ name: "engage-edit" });
    };

    const getVariablesTargets = () => {
        const variables = stage.editTemplateStep.variables;
        // we build an array of variables
        return Array.from(variables).reduce((acc, cur) => {
            const strFormated = cur[1].replace(/^{{|}}$/g, "");
            if (strFormated.includes(".")) {
                const strSplitted = strFormated.split(".");
                strSplitted[0] = strSplitted[0] === "contact" ? "contacts" : strSplitted[0];
                if (!acc[strSplitted[0]]) {
                    acc[strSplitted[0]] = [];
                }
                acc[strSplitted[0]].push(strSplitted[1]);
                return acc;
            } else {
                // the custom variable is the variable writed  by the user
                if (!acc["custom"]) {
                    acc["custom"] = [];
                }
                acc["custom"].push(strFormated);
                return acc;
            }
        }, {});
    };

    const getValidAndInvalidContacts = (textWithoutVar, variablesTargets) => {
        const invalidContacts = [];
        const validContacts = stage.selectContactListStep.contactList.contacts.reduce(
            (acc: ContactInterface[], curr: ContactInterface) => {
                let messageLengtLoop = textWithoutVar.length;
                Object.keys(variablesTargets).map((key) => {
                    variablesTargets[key].map((attr: string) => {
                        if (key === "contacts") {
                            messageLengtLoop += curr[attr] ? curr[attr].length : 0;
                        } else if (key === "custom") {
                            messageLengtLoop += attr.length;
                        } else {
                            const agentData = getUserData();
                            messageLengtLoop += agentData[attr] ? agentData[attr].length : 0;
                        }
                    });
                });
                messageLengtLoop < 1024 ? acc.push(curr) : invalidContacts.push(curr);
                return acc;
            },
            []
        );
        return { validContacts, invalidContacts };
    };

    const manageCharacterMessageLength = () => {
        const textWithoutVar = stage.selectTemplateStep.template.content.replace(/{{.*?}}/g, "").trim();
        const variablesTargets = getVariablesTargets();
        const { validContacts, invalidContacts } = getValidAndInvalidContacts(textWithoutVar, variablesTargets);
        // case template message is too long or we got no valid contacts
        if (textWithoutVar.length > 1024 || validContacts.length === 0) {
            // case where the user can't restrict the contactList to only valid contacts
            bvModal
                .msgBoxOk(
                    i18n.t(
                        textWithoutVar.length > 1024
                            ? "engage.characters-limit.case-template.main-text"
                            : "engage.characters-limit.case-all-contact-invalid.main-text",
                        {
                            limit: 1024,
                            msgLength: textWithoutVar.length,
                        }
                    ),
                    {
                        title: i18n.t(
                            textWithoutVar.length > 1024
                                ? "engage.characters-limit.case-template.title"
                                : "engage.characters-limit.case-all-contact-invalid.title"
                        ),
                        okVariant: "light",
                        okTitle: i18n.t(
                            textWithoutVar.length > 1024
                                ? "engage.characters-limit.case-template.ok-title"
                                : "engage.characters-limit.case-all-contact-invalid.ok-title"
                        ),
                        hideHeaderClose: true,
                        headerCloseVariant: "light",
                        centered: true,
                        bodyClass: "mx-auto",
                        headerClass: "text-center",
                        footerClass: "text-center",
                    }
                )
                .then((result) => {
                    if (result) {
                        previousStep();
                    }
                    return false;
                });
        } else if (invalidContacts.length > 0) {
            // case where the user can restrict the contactList to only valid contacts
            const percentageOfValid = (
                (validContacts.length / stage.selectContactListStep.contactList.contacts.length) *
                100
            ).toFixed(2);

            bvModal
                .msgBoxConfirm(
                    i18n.t("engage.characters-limit.case-part-contacts-valid.main-text", { percentageOfValid }),
                    {
                        title: i18n.t("engage.characters-limit.case-part-contacts-valid.title", { percentageOfValid }),
                        cancelTitle: i18n.t("engage.characters-limit.case-part-contacts-valid.cancel-title"),
                        cancelVariant: "light",
                        okVariant: "light",
                        okTitle: i18n.t("engage.characters-limit.case-part-contacts-valid.ok-title"),
                        hideHeaderClose: true,
                        headerCloseVariant: "light",
                        centered: true,
                        bodyClass: "mx-auto",
                        headerClass: "text-center",
                        footerClass: "text-center",
                    }
                )
                .then(async (result) => {
                    if (result) {
                        const contactListRequestObject = {
                            contactLists: {
                                contacts: validContacts,
                                description: "",
                                name: "temporairy",
                            },
                            updateExisting: false,
                            importedContactData: false,
                        };
                        stage.selectContactListStep.contactList = await createContactList(contactListRequestObject);
                        stage.selectContactListStep.listIsTemporary = false;
                    } else {
                        previousStep();
                    }
                    return false;
                });
        }
    };

    return {
        form: readonly(form),
        currentStep,
        isSending,
        selectedEngageToEdit,
        nextStep,
        previousStep,
        goToStep,
        clearData,
        load,
        selectTemplate,
        selectContactList,
        editTemplate,
        setCampaignAttachments,
        selectChannel,
        sendEnagageMessage,
        selectName,
        selectSchedule,
        getDateTimeSchedule,
        setScheduleIsEnabled,
        editCampaign,
    };
}
